import HomeIcon from "@assets/svg/home-2.svg";
import MapPinIcon from "@assets/svg/map-pin.svg";
import { Button, Spinner } from "@components/index";
import JobsCourses from "@components/jobs-courses";
import SEOHEAD from "@components/SEOHEAD";
import FloatScrollButton from "@components/ui/FloatScrollButton";
import { CooperationTypeBy } from "@interfaces/index";
import ArrowLeft2 from "assets/svg/arrow-left-2.svg";
import gate from "gate";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { FC } from "react";
import { dehydrate, QueryClient, useQuery } from "react-query";

import { convertMiladiToJalali } from "../../src/utils";

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const queryClient = new QueryClient();
  const id = ctx?.params?.id?.[0];
  try {
    await queryClient.fetchQuery([`recommended-entity-for-job`], () =>
      gate.recommendedEntityForJob()
    );
    await queryClient.fetchQuery([`newest-jobs`], () => gate.newAdvertisings());
    await queryClient.fetchQuery([`job-detail`], () => gate.jobGetById(id));
  } catch (error: any) {
    ctx.res.statusCode = error?.response?.status;
    console.log("---------------->", error);
    return {
      notFound: true,
    };
  }
  return {
    props: {
      id: id,
      dehydratedState: dehydrate(queryClient),
    },
  };
};

type Props = {
  id: any;
};

const Index: FC<Props> = ({ id }) => {
  const { push }: any = useRouter();
  const { data: dataRecommendedEntity, isLoading: isLoadingRecommendedEntity } =
    useQuery(
      [`recommended-entity-for-job`],
      () => gate.recommendedEntityForJob(),
      {
        select: (data: any) => data?.data.output,
        refetchOnWindowFocus: false,
        onError: (error) => console.log(error),
      }
    );
  const { data: dataNewest, isLoading: isLoadingNewest } = useQuery(
    [`newest-jobs`],
    () => gate.newAdvertisings(),
    {
      select: (data: any) => data?.data,
      refetchOnWindowFocus: false,
      onError: (error) => console.log(error),
    }
  );
  const { data: dataDetail, isLoading: isLoadingDetail } = useQuery(
    [`job-detail`],
    () => gate.jobGetById(id),
    {
      select: (data: any) => data?.data,
      refetchOnWindowFocus: false,
      onError: (error) => console.log(error),
    }
  );
  const getCooperationBadge = (cooperationType: number) => {
    switch (cooperationType) {
      case CooperationTypeBy.FullTime: {
        return (
          <div className="rounded bg-[#4F9B0B3D] px-2 py-1 text-xs font-bold text-[#4F9B0B]">
            تمام وقت
          </div>
        );
      }
      case CooperationTypeBy.HalfTime: {
        return (
          <div className="rounded bg-[#FBCF3238] px-2 py-1 text-xs font-bold text-[#F0BC01]">
            پاره وقت
          </div>
        );
      }
      case CooperationTypeBy.Project: {
        return (
          <div className="rounded bg-[#FCEAD7] px-2 py-1 text-xs font-bold text-[#EE8A1E]">
            پروژه ای
          </div>
        );
      }
    }
  };

  return (
    <>
      <SEOHEAD
        {...{
          canonical: `https://dadbanan.com/job/${id}/${
            dataDetail?.jobTitle?.trim()?.replaceAll(" ", "-") || ""
          }`,
          keyword: dataDetail?.seoKeyword as [],
          seoDescription: dataDetail?.jobTitle,
          title: `${dataDetail?.jobTitle ?? ""} | مؤسسه دادبانان`,
        }}
      />
      {isLoadingDetail ? (
        <div className="grid h-[80vh] place-items-center">
          <Spinner className="mx-auto my-auto h-8 w-8" />
        </div>
      ) : (
        <div className="min-h-screen w-full bg-[#FAFAFC]">
          <div
            className="h-auto bg-[length:100%_83px] lg:bg-[length:100%_194px]"
            style={{
              backgroundImage: `url('/images/pages/jobs/detail-bg.jpg')`,
              backgroundRepeat: "no-repeat",
            }}
          >
            <div className="h-auto w-full pb-[10px] pt-10 lg:pt-24">
              <div className="mx-auto grid h-auto w-full grid-cols-1 justify-center gap-x-5 px-[4%] xl:grid-cols-[_53.6%,_24.6%] xl:px-0">
                <div>
                  <div className="ml-5 h-min w-full rounded bg-white p-5 shadow">
                    <div className="mx-2 md:mx-6">
                      <div className="relative flex items-center justify-between">
                        <h1
                          className={`text-lg font-bold lg:text-xl ${
                            !dataDetail?.isEnable ? "pl-20" : ""
                          }`}
                        >
                          {dataDetail?.jobTitle}
                        </h1>
                        <p className="hidden text-lg lg:block">
                          تاریخ انتشار:{" "}
                          {convertMiladiToJalali(dataDetail?.publishDate)}
                        </p>
                        {!dataDetail?.isEnable ? (
                          <img
                            src="/images/closed.png"
                            className="absolute top-0 left-0 lg:left-5 lg:top-10"
                            alt="closed"
                            loading="lazy"
                          />
                        ) : undefined}
                      </div>
                      <div className="mt-4 flex flex-col items-center gap-2 md:mt-6 md:flex-row md:gap-6">
                        <p className="w-full text-base md:w-auto md:text-lg">
                          حقوق دریافتی : {dataDetail?.salary}
                        </p>
                        {dataDetail?.workExperience && dataDetail?.workExperience.length && +dataDetail?.workExperience ? (
                          <p className="w-full text-xs font-bold text-blue-550 md:w-auto">
                            سابقه {dataDetail?.workExperience} سال
                          </p>
                        ) : undefined}
                      </div>
                      <div className="mt-4 flex gap-3 md:mt-6">
                        {getCooperationBadge(dataDetail?.cooperationType)}
                        {dataDetail?.remote ? (
                          <div className="rounded bg-[#0577AF42] px-2 py-1 text-xs font-bold text-[#0577AF]">
                            دورکاری
                          </div>
                        ) : (
                          <div className="rounded bg-[#77268B40] px-2 py-1 text-xs font-bold text-[#77268B]">
                            حضوری
                          </div>
                        )}
                      </div>
                      <p className="mt-4 text-sm md:mt-6">
                        کد اگهی :{dataDetail?.advertisingCode}
                      </p>
                    </div>
                    <hr className="mt-3 w-full border-[#99969633]" />
                    <div className="mx-2 md:mx-6">
                      <div className="mt-4 flex justify-between gap-0 md:mt-6 md:justify-start md:gap-32 ">
                        <div className="flex items-center gap-2 text-base font-bold text-black md:text-lg">
                          <HomeIcon />
                          {dataDetail?.organizationName}
                        </div>
                        <div className="flex items-center gap-2  text-base font-bold text-black md:text-lg">
                          <MapPinIcon />
                          {dataDetail?.city} / {dataDetail?.neighbourhood}
                        </div>
                      </div>
                      <div className="mt-4 text-base md:mt-6 md:text-lg">
                        <p className="font-bold">صنعت سازمان</p>
                        <p>{dataDetail?.industryOrganiazation}</p>
                      </div>
                      {dataDetail?.organiazationActivityDescription &&
                      dataDetail?.organiazationActivityDescription.length ? (
                        <div className="mt-4 text-base md:mt-6 md:text-lg">
                          <p className="font-bold">معرفی سازمان</p>
                          <div
                            className="mt-1 mb-6 text-justify"
                            dangerouslySetInnerHTML={{
                              __html:
                                dataDetail?.organiazationActivityDescription,
                            }}
                          />
                        </div>
                      ) : undefined}
                    </div>
                    <hr className="mt-4 w-full border-[#99969633] md:mt-6" />
                    <div className="mx-2 md:mx-6">
                      <p className="mt-4 text-base font-bold text-blue-550 md:mt-6 md:text-lg">
                        شرح شغل وظایف
                      </p>
                      <div
                        className="my-6 text-justify"
                        dangerouslySetInnerHTML={{
                          __html: dataDetail?.jobDescription,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <aside className="mt-5 flex w-full flex-col-reverse xl:mt-0 xl:flex-col">
                  <div className="top-5 xl:sticky">
                    <div className="space-y-6 rounded bg-white p-5 text-xs text-black-450 shadow md:text-sm xl:text-base">
                      <p className="text-center text-sm font-bold text-black md:text-lg">
                        ارسال رزومه برای {dataDetail?.organizationName} از طریق
                        جاب ویژن
                      </p>
                      <Button
                        onClick={() => push(dataDetail?.jobLink)}
                        disabled={!dataDetail?.isEnable}
                        CClassName={`bg-blue-550 rounded-lg disabled:text-[#FF0000] disabled:cursor-not-allowed disabled:border disabled:border-[#FF0000] disabled:bg-[#FF00002B] pt-[10px] font-bold pb-[14px] text-[14px] 2xl:text-base w-full text-white`}
                      >
                        {dataDetail?.isEnable ? (
                          <>ارسال رزومه</>
                        ) : (
                          <>این آگهی بسته شده است.</>
                        )}
                      </Button>
                    </div>
                    {dataNewest?.length ? (
                      <div
                        className={`mt-5 w-full rounded bg-white shadow xl:mb-0`}
                      >
                        {/*relatedPostsWrapper*/}
                        {/*<Scrollbar>*/}
                        <div className="w-full rounded bg-white px-5">
                          <h2 className="py-5 text-sm text-blue-550 md:text-base xl:text-lg">
                            جدیدترین شغل ها
                          </h2>
                          <ul className="w-full space-y-5">
                            {dataNewest?.map((item: any) => (
                              <li
                                key={JSON.stringify(item?.id)}
                                className="border-b border-[#C4C4C4] pb-5 last:border-0"
                              >
                                <h3 className="mb-[10px] text-xs md:text-sm xl:text-base">
                                  {item?.jobTitle}
                                </h3>
                                <div className="flex justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="flex gap-2">
                                      {getCooperationBadge(
                                        item.cooperationType
                                      )}
                                      {dataDetail?.remote ? (
                                        <div className="rounded bg-[#0577AF42] px-2 py-1 text-xs font-bold text-[#0577AF]">
                                          دورکاری
                                        </div>
                                      ) : (
                                        <div className="rounded bg-[#77268B40] px-2 py-1 text-xs font-bold text-[#77268B]">
                                          حضوری
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-2 text-xs font-bold">
                                      <div className="text-black-550">
                                        حقوق :
                                      </div>
                                      <div className="text-blue-550">
                                        {item.salary}
                                      </div>
                                    </div>
                                  </div>
                                  <Link
                                    href={`/job/${item.id}/${item.jobTitle
                                      .trim()
                                      .replaceAll(" ", "-")}`}
                                  >
                                    <a>
                                      <ArrowLeft2 />
                                    </a>
                                  </Link>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                        {dataRecommendedEntity?.recentlyPosts?.length ? (
                          <div className="w-full rounded bg-white px-5">
                            <h2 className="py-5 text-sm text-blue-550 md:text-base xl:text-lg">
                              {/*آخرین {renderType(dataRecommendedEntity?.template, true)}*/}
                            </h2>
                            <ul className="w-full space-y-5">
                              {dataRecommendedEntity?.recentlyPosts?.map(
                                (item: any) => (
                                  <li
                                    // onClick={() => push(`/post/${item?.id}`)}
                                    key={JSON.stringify(item?.id)}
                                    className="grid cursor-pointer grid-cols-[_.8fr,_2fr] gap-x-2 border-b border-[#C4C4C4] pb-5 last:border-0"
                                  >
                                    <img
                                      loading="lazy"
                                      src={item?.image}
                                      alt={item?.title}
                                      className="aspect-auto max-w-[106px] self-center rounded object-contain"
                                    />
                                    <div className="flex flex-col">
                                      <h3 className="mb-[10px] text-xs md:text-sm xl:text-base">
                                        {item?.title}
                                      </h3>
                                      <span className="text-xs text-[#919191] xl:text-sm">
                                        {item?.writer && (
                                          <>
                                            <span>
                                              پدیدآورنده {item?.writer}.{" "}
                                            </span>
                                          </>
                                        )}
                                        {item?.viewCount ? (
                                          <>
                                            <span>
                                              {item?.viewCount} بازدید
                                            </span>
                                          </>
                                        ) : undefined}
                                      </span>
                                    </div>
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        ) : undefined}
                        {/*</Scrollbar>*/}
                      </div>
                    ) : undefined}
                  </div>
                </aside>
              </div>

              <article className="px-[4%] py-12 lg:px-[10.3%]">
                <JobsCourses
                  data={dataRecommendedEntity}
                  title={"دوره ها و بسته های مرتبط با مشاغل"}
                />
              </article>
            </div>
          </div>
        </div>
      )}
      <FloatScrollButton />
    </>
  );
};

export default Index;
